import type { ReactNode } from 'react';
import type { KpiExplain } from '../lib/kpiExplain';
import { useKpiExplain } from './KpiExplainProvider';

type Props = {
  label: string;
  value: ReactNode;
  hint?: string;
  emphasis?: boolean;
  /** Highlight card and show a "Net worth" badge — primary summary metric for a view. */
  netWorthHeadline?: boolean;
  emoji?: string;
  valueWrap?: boolean;
  /** Opens the KPI explain sidebar with formula and a worked example. */
  kpiExplain?: KpiExplain;
  valueMuted?: boolean;
};

function KpiInfoButton({ label, explain }: { label: string; explain: KpiExplain }) {
  const { open } = useKpiExplain();

  return (
    <button
      type="button"
      className="stat-kpi-info"
      aria-label={`How ${label} is calculated`}
      title={`How ${label} is calculated`}
      onClick={(event) => {
        event.stopPropagation();
        open({ title: label, explain });
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
        <path
          d="M12 11v5M12 8h.01"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

export function StatCard({
  label,
  value,
  hint,
  emphasis,
  netWorthHeadline,
  emoji,
  valueWrap,
  kpiExplain,
  valueMuted,
}: Props) {
  const highlighted = emphasis || netWorthHeadline;

  return (
    <div className={`stat-card${highlighted ? ' stat-card-emphasis' : ''}`}>
      {netWorthHeadline ? (
        <span className="stat-net-worth-badge">Net worth</span>
      ) : null}
      <div className="stat-label-row">
        <div className="stat-label">{label}</div>
        {kpiExplain ? <KpiInfoButton label={label} explain={kpiExplain} /> : null}
      </div>
      <div
        className={`stat-value${valueWrap ? ' stat-value--wrap' : ''}${valueMuted ? ' muted' : ''}`}
      >
        {emoji ? (
          <span className="stat-emoji" aria-hidden>
            {emoji}
          </span>
        ) : null}
        {typeof value === 'string' ? <span>{value}</span> : value}
      </div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}
